import { useMutualDashboardContext } from "api/dashboard/mutual/MutualDashboardProvider";
import { usePersonContext } from "api/directory/person/PersonProvider";
import { useBranchContext } from "api/settings/branch/BranchProviders";
import { useJobContext } from "api/settings/job/JobProvider";
import { useJobCategoryContext } from "api/settings/job-category/JobCategoryProvider";
import { useUserContext } from "api/user/UserProvider";
import AlertPopup from "components/alert-popup/AlertPopup";
import SharedDashboardByFilters from "components/form/shared-dashboard/SharedDashboardByFilters";
import SharedDashboardForm from "components/form/shared-dashboard/SharedDashboardForm";
import {
  DASHBOARD_SHARED_SECTIONS,
  FILTER_KEYS,
  QUERY_STATUS,
} from "constants/constants";
import DocumentDownloadModal from "components/modal/DocumentDownloadModal";
import {
  SHARED_DASHBOARD_FORM_DEFAULT_VALUES,
  SHARED_DASHBOARD_FORM_LABELS,
} from "constants/forms";
import { INFO_HEADER_NAVIGATION } from "constants/info-header";
import InfoContext from "pages/InfoContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  currentDateFormatted,
  isObjectFiltersEmpty,
  lastDayOfTheMonthFormatted,
  requestValueFormatter,
} from "utils/sharedDashboardHelper";
import { getJobsInCategories } from "utils/functions";
import NavigationBar from "components/navigation-bar/NavigationBar";
import { useLocalStorage } from "react-use";
import { useStaffContext } from "api/directory/staff/StaffProvider";

const SharedDashboard = () => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
    clearErrors,
  } = useForm({
    defaultValues: SHARED_DASHBOARD_FORM_DEFAULT_VALUES,
  });

  const {
    lang,
    userScope,
    postUserFilters,
    getUserFilters,
    userFilterStorage,
    connectedUser,
  } = useUserContext();

  const {
    mutualDashboardICDataGetter,
    mutualDashboardICData,
    mutualDashboardFDMDataGetter,
    mutualDashboardFDMData,
    mutualDashboardEDData,
    mutualDashboardEDDataGetter,
    mutualDashboardExitsData,
    mutualDashboardExitsDataGetter,
    mutualDashboardCLData,
    mutualDashboardCLDataGetter,
    mutualDashboardOKData,
    mutualDashboardOKDataGetter,
    loadICDataStatus,
    loadFDMDataStatus,
    loadEDDataStatus,
    loadOKDataStatus,
    loadCLDataStatus,
    loadExitsDataStatus,
    setMutualDashboardICData,
    setMutualDashboardFDMData,
    setMutualDashboardEDData,
    setMutualDashboardExitsData,
    setMutualDashboardCLData,
    setMutualDashboardOKData,
    getMutualDashboardCSVFile,
    loadExportStatus,
    setLoadExportStatus,
  } = useMutualDashboardContext();
  const { branchList, branchListGetter } = useBranchContext();
  const { getPersonsByProfile, personsByProfileList } = usePersonContext();
  const { jobList, jobListGetter } = useJobContext();
  const { getCategoriesJobIds, jobCategoryList, jobCategoryListGetter } =
    useJobCategoryContext();
  const { getStaffById, staffById } = useStaffContext();
  const [isValidated, setIsValidated] = useState(false);
  const [code, setCode] = useState();
  const [status, setStatus] = useState();
  const sections = INFO_HEADER_NAVIGATION.sharedDashboard;
  const { ic, ed, ko } = DASHBOARD_SHARED_SECTIONS || {};
  const [section, setSection] = useState(ic);
  const firstDayOfNextMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth() + 1,
    2
  );
  const firstDayOfNextMonthFormatted = firstDayOfNextMonth
    .toISOString()
    .split("T")[0];

  useEffect(() => {
    branchListGetter();
    jobListGetter();
    jobCategoryListGetter();
    getUserFilters(FILTER_KEYS.SHARED_DASHBOARD);
    getStaffById(connectedUser?.id);
  }, []);

  const connectedUserBranch = branchList?.find(
    (e) => e.id === staffById?.branch_id
  );
  const { id: connectedUserBranchId, name: connectedUserBranchName } =
    connectedUserBranch || {};

  const [filterIC, setFilterIC] = useLocalStorage(
    "shared_dashboard_ic_filters",
    {
      intercontracts_subsidiaries: [],
      intercontracts_managers: [],
      intercontracts_domain_categories: [],
      intercontracts_domains: [],
    }
  );
  const [filterFDM, setFilterFDM] = useLocalStorage(
    "shared_dashboard_fdm_filters",
    {
      missions_exits_activation_subsidiaries: [],
      missions_exits_activation_managers: [],
      missions_exits_activation_domain_categories: [],
      missions_exits_activation_domains: [],
    }
  );
  const [filterED, setFilterED] = useLocalStorage(
    "shared_dashboard_ed_filters",
    {
      direct_hiring_subsidiaries: [],
      manager_mission_ed: [],
      dav_function_category_ed: [],
      dav_function_ed: [],
      date: "",
      mobility: "",
    }
  );
  const [filterOK, setFilterOK] = useLocalStorage(
    "shared_dashboard_ok_filters",
    {
      ok_subsidiaries: [],
      ok_managers: [],
    }
  );
  const [filterCL, setFilterCL] = useLocalStorage(
    "shared_dashboard_cl_filters",
    {
      long_leave_subsidiaries: [],
      long_leave_managers: [],
    }
  );
  const [filterKO, setFilterSS] = useLocalStorage(
    "shared_dashboard_ss_filters",
    {
      companies_exits_subsidiaries: [],
      manager_companies_exits: [],
      companies_exits_date: "",
      exits_companies_by: "",
    }
  );

  const getTabSubsidiaryDefault = useCallback(
    (tabKey) => {
      if (
        !connectedUserBranchId ||
        !connectedUserBranchName ||
        !connectedUser?.id
      ) {
        return [];
      }

      const USER_ID_KEY = "shared_dashboard_user_id";
      const storedUserId = localStorage.getItem(USER_ID_KEY);
      const contractualBranch = {
        value: connectedUserBranchId,
        label: connectedUserBranchName,
      };

      if (storedUserId !== String(connectedUser.id)) {
        ["ic", "fdm", "ed", "ok", "cl", "ko"].forEach((tab) => {
          localStorage.removeItem(`shared_dashboard_${tab}_subsidiary`);
        });
        localStorage.setItem(USER_ID_KEY, String(connectedUser.id));
        return [contractualBranch];
      }
      const tabStorageKey = `shared_dashboard_${tabKey}_subsidiary`;
      const storedSubsidiary = localStorage.getItem(tabStorageKey);

      if (storedSubsidiary) {
        try {
          const parsed = JSON.parse(storedSubsidiary);
          return Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          return [contractualBranch];
        }
      }
      return [contractualBranch];
    },
    [connectedUserBranchId, connectedUserBranchName, connectedUser?.id]
  );

  const defaultBranches = useMemo(
    () => ({
      ic: getTabSubsidiaryDefault("ic"),
      fdm: getTabSubsidiaryDefault("fdm"),
      ed: getTabSubsidiaryDefault("ed"),
      ok: getTabSubsidiaryDefault("ok"),
      cl: getTabSubsidiaryDefault("cl"),
      ko: getTabSubsidiaryDefault("ko"),
    }),
    [getTabSubsidiaryDefault]
  );

  const directHiringMobility = watch("mobility", "");
  const intercontractsSubsidiaries = watch(
    "intercontracts_subsidiaries",
    defaultBranches.ic
  );
  const intercontractsManagers = watch("intercontracts_managers", []);
  const intercontractsStartDate = watch(
    "intercontracts_start_date",
    currentDateFormatted
  );
  const intercontractsDomainCategories = watch(
    "intercontracts_domain_categories",
    []
  );
  const intercontractsDomains = watch("intercontracts_domains", []);
  const missionsExitsActivationSubsidiaries = watch(
    "missions_exits_activation_subsidiaries",
    defaultBranches.fdm
  );
  const missionsExitsActivationManagers = watch(
    "missions_exits_activation_managers",
    []
  );
  const missionsExitsActivationDomainCategories = watch(
    "missions_exits_activation_domain_categories",
    []
  );
  const missionsExitsActivationDomains = watch(
    "missions_exits_activation_domains",
    []
  );
  const companiesExitsCompaniesBy = watch("exits_companies_by", "all");
  const directHiringSubsidiaries = watch(
    "direct_hiring_subsidiaries",
    defaultBranches.ed
  );
  const directHiringManagers = watch("direct_hiring_managers", []);
  const directHiringDomainCategories = watch(
    "direct_hiring_domain_categories",
    []
  );
  const directHiringDomains = watch("direct_hiring_domains", []);
  const directHiringDate = watch(
    "direct_hiring_date",
    firstDayOfNextMonthFormatted
  );
  const fdmDate = watch("missions_exits_activation_date", "");
  const okSubsidiaries = watch("ok_subsidiaries", defaultBranches.ok);
  const okManagers = watch("ok_managers", []);
  const okStartAt = watch("ok_start_at", []);
  const longLeaveSubsidiaries = watch(
    "long_leave_subsidiaries",
    defaultBranches.cl
  );
  const longLeaveManagers = watch("long_leave_managers", []);
  const longLeaveDate = watch("long_leave_date", "");
  const okDate = watch("ok_date", firstDayOfNextMonthFormatted);
  const companiesExitsSubsidiaries = watch(
    "companies_exits_subsidiaries",
    defaultBranches.ko
  );
  const companiesExitsManagers = watch("companies_exits_managers", []);
  const companiesExitsDate = watch(
    "companies_exits_date",
    lastDayOfTheMonthFormatted
  );
  const [isInitialized, setIsInitialized] = useState(false);

  const setFiltersPriority = (key, setFilter, dbFilters) => {
    const storedValue = localStorage.getItem(key);
    if (storedValue) {
      const parsed = JSON.parse(storedValue);
      if (isObjectFiltersEmpty(parsed)) {
        if (!isObjectFiltersEmpty(dbFilters)) {
          setFilter(dbFilters);
          localStorage.setItem(key, JSON.stringify(dbFilters));
        } else {
          setFilter({});
        }
      } else {
        setFilter(parsed);
      }
    } else {
      if (!isObjectFiltersEmpty(dbFilters)) {
        setFilter(dbFilters);
        localStorage.setItem(key, JSON.stringify(dbFilters));
      } else {
        setFilter({});
      }
    }
  };

  useEffect(() => {
    if (!userFilterStorage) return;
    connectedUserBranchId && setIsInitialized(true);
  }, [userFilterStorage, userScope, connectedUserBranch]);

  useEffect(() => {
    if (!userFilterStorage) return;
    const { ic, fdm, ed, ok, cl, ko } = userFilterStorage;
    setFiltersPriority("shared_dashboard_ic_filters", setFilterIC, ic);
    setFiltersPriority("shared_dashboard_fdm_filters", setFilterFDM, fdm);
    setFiltersPriority("shared_dashboard_ed_filters", setFilterED, ed);
    setFiltersPriority("shared_dashboard_ok_filters", setFilterOK, ok);
    setFiltersPriority("shared_dashboard_cl_filters", setFilterCL, cl);
    setFiltersPriority("shared_dashboard_ss_filters", setFilterSS, ko);
  }, [userFilterStorage]);

  const infosBySection = {
    ic: {
      infos: mutualDashboardICData,
      infosGetter: mutualDashboardICDataGetter,
      loadDataStatus: loadICDataStatus,
      filters: {
        date: intercontractsStartDate,
        subsidiary: intercontractsSubsidiaries,
        manager: intercontractsManagers,
        jobCategory: intercontractsDomainCategories,
        job: intercontractsDomains,
      },
      setter: setMutualDashboardICData,
      sectionName: "intercontracts",
      inputDateLabel: SHARED_DASHBOARD_FORM_LABELS.ic_start,
      inputDateSetter: (value) => setValue("intercontracts_start_date", value),
      inputName: "intercontracts_start_date",
    },
    fdm: {
      infos: mutualDashboardFDMData,
      infosGetter: mutualDashboardFDMDataGetter,
      loadDataStatus: loadFDMDataStatus,
      filters: {
        date: fdmDate,
        subsidiary: missionsExitsActivationSubsidiaries,
        manager: missionsExitsActivationManagers,
        jobCategory: missionsExitsActivationDomainCategories,
        job: missionsExitsActivationDomains,
      },
      setter: setMutualDashboardFDMData,
      sectionName: "missions_exits_activation",
      inputDateLabel: "missions_exits_activation_date",
      inputDateSetter: (value) =>
        setValue("missions_exits_activation_date", value),
      inputName: "missions_exits_activation_date",
    },
    ed: {
      infos: mutualDashboardEDData,
      infosGetter: mutualDashboardEDDataGetter,
      loadDataStatus: loadEDDataStatus,
      filters: {
        date: directHiringDate || firstDayOfNextMonthFormatted,
        subsidiary: directHiringSubsidiaries,
        manager: directHiringManagers,
        jobCategory: directHiringDomainCategories,
        job: directHiringDomains,
        mobility: directHiringMobility,
      },
      setter: setMutualDashboardEDData,
      sectionName: "direct_hiring",
      inputDateLabel: SHARED_DASHBOARD_FORM_LABELS.date,
      inputDateSetter: (value) => setValue("direct_hiring_date", value),
      inputName: "direct_hiring_date",
    },
    ok: {
      infos: mutualDashboardOKData,
      infosGetter: mutualDashboardOKDataGetter,
      loadDataStatus: loadOKDataStatus,
      filters: {
        date: okDate,
        subsidiary: okSubsidiaries,
        manager: okManagers,
        start_at: okStartAt,
      },
      setter: setMutualDashboardOKData,
      sectionName: "ok",
      inputDateLabel: SHARED_DASHBOARD_FORM_LABELS.after_date_of,
      inputDateSetter: (value) => setValue("ok_date", value),
      inputName: "ok_date",
    },
    cl: {
      infos: mutualDashboardCLData,
      infosGetter: mutualDashboardCLDataGetter,
      loadDataStatus: loadCLDataStatus,
      filters: {
        date: longLeaveDate,
        subsidiary: longLeaveSubsidiaries,
        manager: longLeaveManagers,
      },
      setter: setMutualDashboardCLData,
      sectionName: "long_leave",
      inputDateLabel: SHARED_DASHBOARD_FORM_LABELS.date,
      inputDateSetter: (value) => setValue("long_leave_date", value),
      inputName: "long_leave_date",
    },
    ko: {
      infos: mutualDashboardExitsData,
      infosGetter: mutualDashboardExitsDataGetter,
      loadDataStatus: loadExitsDataStatus,
      filters: {
        date: companiesExitsDate,
        subsidiary: companiesExitsSubsidiaries,
        manager: companiesExitsManagers,
        exits_companies_by: companiesExitsCompaniesBy,
      },
      setter: setMutualDashboardExitsData,
      sectionName: "companies_exits",
      inputDateLabel: SHARED_DASHBOARD_FORM_LABELS.date,
      inputDateSetter: (value) => setValue("companies_exits_date", value),
      inputName: "companies_exits_date",
    },
  };

  useEffect(() => {
    if (isValidated) {
      const allFilters = {
        ic: filterIC,
        fdm: filterFDM,
        ed: filterED,
        ok: filterOK,
        cl: filterCL,
        ko: filterKO,
      };

      postUserFilters(
        {
          key: FILTER_KEYS.SHARED_DASHBOARD,
          value: JSON.stringify(allFilters),
        },
        FILTER_KEYS.SHARED_DASHBOARD
      );
      setIsValidated(false);
    }
  }, [isValidated]);

  const mutualDashboardGetter = (page) => {
    const {
      subsidiary,
      date,
      manager,
      job,
      jobCategory,
      directHiringMobility,
      start_at,
      exits_companies_by,
    } = infosBySection[section].filters || {};
    !page && infosBySection[section].setter({});
    infosBySection[section].infosGetter({
      subsidiary: requestValueFormatter(subsidiary),
      date,
      manager: requestValueFormatter(manager),
      jobCategory: requestValueFormatter(jobCategory),
      job: requestValueFormatter(job),
      mobility: directHiringMobility,
      start_at: requestValueFormatter(start_at),
      exits_companies_by,
      worker_type: exits_companies_by,
      page: page || 1,
    });
  };

  const getOptions = (search, profile = "") => {
    getPersonsByProfile({ search, profile });
  };

  // A job only comes back with a category that offers it (redmine 12939). A
  // stored pair can be incoherent two ways: it predates the feature, when the
  // category was optional, or the user just removed a category — this effect
  // runs after the form pruned itself, and re-applying the stored list would
  // undo that prune. Either way the job field is locked or narrowed, so the
  // filter would be applied to every search with no way to remove it by hand.
  // Waits for the referential: an empty one reads as "offers nothing".
  const restoredJobs = (storedCategories, storedJobs) => {
    if (!storedJobs?.length || !storedCategories?.length) return [];
    if (!jobCategoryList?.length) return storedJobs;
    return getJobsInCategories(
      storedJobs,
      getCategoriesJobIds(
        storedCategories.map((elm) => elm?.value).filter(Boolean)
      )
    );
  };

  useEffect(() => {
    if (filterIC.intercontracts_subsidiaries?.length) setValue("intercontracts_subsidiaries", filterIC.intercontracts_subsidiaries);
    setValue("intercontracts_managers", filterIC.intercontracts_managers || []);
    setValue(
      "intercontracts_domain_categories",
      filterIC.intercontracts_domain_categories || []
    );
    setValue(
      "intercontracts_domains",
      restoredJobs(
        filterIC.intercontracts_domain_categories,
        filterIC.intercontracts_domains
      )
    );
    if (filterFDM.missions_exits_activation_subsidiaries?.length)
      setValue(
        "missions_exits_activation_subsidiaries",
        filterFDM.missions_exits_activation_subsidiaries
      );
    setValue(
      "missions_exits_activation_managers",
      filterFDM.missions_exits_activation_managers || []
    );
    setValue(
      "missions_exits_activation_domain_categories",
      filterFDM.missions_exits_activation_domain_categories || []
    );
    setValue(
      "missions_exits_activation_domains",
      restoredJobs(
        filterFDM.missions_exits_activation_domain_categories,
        filterFDM.missions_exits_activation_domains
      )
    );
    if (filterED.direct_hiring_subsidiaries?.length)
      setValue(
        "direct_hiring_subsidiaries",
        filterED.direct_hiring_subsidiaries
      );
    setValue("direct_hiring_date", filterED.date);
    setValue("direct_hiring_managers", filterED.manager_mission_ed);
    setValue(
      "direct_hiring_domain_categories",
      filterED.dav_function_category_ed || []
    );
    setValue(
      "direct_hiring_domains",
      restoredJobs(filterED.dav_function_category_ed, filterED.dav_function_ed)
    );
    setValue("mobility", filterED.mobility || "");
    if (filterOK.ok_subsidiaries?.length) setValue("ok_subsidiaries", filterOK.ok_subsidiaries);
    setValue("ok_managers", filterOK.ok_managers || []);
    if (filterCL.long_leave_subsidiaries?.length) setValue("long_leave_subsidiaries", filterCL.long_leave_subsidiaries);
    setValue("long_leave_managers", filterCL.long_leave_managers || []);
    if (filterKO.companies_exits_subsidiaries?.length) setValue("companies_exits_subsidiaries", filterKO.companies_exits_subsidiaries);
    setValue("companies_exits_managers", filterKO.manager_companies_exits);
    setValue("companies_exits_date", filterKO.companies_exits_date);
    setValue("exits_companies_by", filterKO.exits_companies_by);
    setIsInitialized(false);
  }, [section, isInitialized]);

  useEffect(() => {
    if (
      infosBySection[section].loadDataStatus !== QUERY_STATUS.LOADING &&
      isInitialized
    ) {
      mutualDashboardGetter();
    }
  }, [userScope, isInitialized]);

  const onSubmit = ({ page }) => {
    mutualDashboardGetter(page);
  };

  const values = useMemo(
    () => ({
      filterIC,
      filterFDM,
      filterED,
      filterOK,
      filterCL,
      filterKO,
      setFilterIC,
      setFilterFDM,
      setFilterED,
      setFilterOK,
      setFilterCL,
      setFilterSS,
      userScope,
      sections,
      setSection,
      section,
      activeSection: section,
      setCode,
      setStatus,
      control,
      errors,
      handleSubmit,
      register,
      setValue,
      watch,
      clearErrors,
      branchList,
      getOptions,
      personsByProfileList,
      jobList,
      jobCategoryList,
      getCategoriesJobIds,
      lang,
      loadICDataStatus,
      loadFDMDataStatus,
      loadEDDataStatus,
      loadOKDataStatus,
      loadCLDataStatus,
      loadExitsDataStatus,
      mutualDashboardICData,
      mutualDashboardFDMData,
      mutualDashboardEDData,
      mutualDashboardOKData,
      mutualDashboardCLData,
      mutualDashboardExitsData,
      onSubmit,
      companiesExitsCompaniesBy,
      infosBySection,
      setIsValidated,
      getMutualDashboardCSVFile,
      loadExportStatus,
      setLoadExportStatus,
    }),
    [
      filterIC,
      filterFDM,
      filterED,
      filterOK,
      filterCL,
      filterKO,
      userScope,
      sections,
      section,
      control,
      errors,
      handleSubmit,
      register,
      setValue,
      watch,
      clearErrors,
      branchList,
      personsByProfileList,
      jobList,
      jobCategoryList,
      getCategoriesJobIds,
      lang,
      loadICDataStatus,
      loadFDMDataStatus,
      loadEDDataStatus,
      loadOKDataStatus,
      loadCLDataStatus,
      loadExitsDataStatus,
      mutualDashboardICData,
      mutualDashboardFDMData,
      mutualDashboardEDData,
      mutualDashboardOKData,
      mutualDashboardCLData,
      mutualDashboardExitsData,
      companiesExitsCompaniesBy,
      infosBySection,
      getMutualDashboardCSVFile,
      loadExportStatus,
      setLoadExportStatus,
    ]
  );
  return (
    <InfoContext.Provider value={values}>
      <DocumentDownloadModal
        status={loadExportStatus}
        onClose={() => setLoadExportStatus(QUERY_STATUS.INITIAL)}
      />
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <NavigationBar />
      <SharedDashboardForm />
      <SharedDashboardByFilters />
    </InfoContext.Provider>
  );
};

export default SharedDashboard;
